import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { invoiceCostLKR } from '../../utils/costPax';
import { downloadCsv } from '../../utils/downloadCsv';

// Agent Sales — every non-void invoice since the start, per agent: customer, supplier(s),
// nett cost, selling price, GP, margin and commission earned. An invoice belongs to an
// agent if the agent is the client's care-of agent OR holds a commission line on it (one
// invoice can show under more than one agent when commission is split).
// GP/margin use the same cost rule as the invoice page (utils/costPax.js).

const PAGE = 1000;
async function fetchAllPaged(build) {
  let all = [], from = 0;
  while (true) {
    const { data, error } = await build().range(from, from + PAGE - 1);
    if (error) throw error;
    if (!data || !data.length) break;
    all = all.concat(data);
    if (data.length < PAGE) break;
    from += PAGE;
  }
  return all;
}

const fmt = (n) => (n || 0).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const TYPE_LABEL = { ticket: 'Ticket', visa: 'Visa', booking: 'Package' };

export default function AgentSales() {
  const [params, setParams] = useSearchParams();
  const agentFilter = params.get('agent') || '';
  const [agents, setAgents] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true); setError('');
      try {
        const [ags, invs, comms] = await Promise.all([
          fetchAllPaged(() => supabase.from('agents').select('id, tls_code, full_name').order('tls_code')),
          fetchAllPaged(() => supabase.from('invoices')
            .select('id, invoice_number, issued_date, status, source_type, line_items, cost_items, clients(full_name, client_code, care_of_agent_id)')
            .not('status', 'in', '(VOID,CANCELLED)')
            .order('issued_date', { ascending: false })),
          fetchAllPaged(() => supabase.from('commissions')
            .select('reference_id, agent_ref_id, commission_amount, commission_status')),
        ]);
        setAgents(ags);

        // commission per (invoice, agent)
        const commByKey = {};
        for (const c of comms) {
          if (!c.reference_id || !c.agent_ref_id) continue;
          const k = `${c.reference_id}|${c.agent_ref_id}`;
          const e = commByKey[k] || (commByKey[k] = { amount: 0, statuses: new Set() });
          e.amount += parseFloat(c.commission_amount) || 0;
          if (c.commission_status) e.statuses.add(c.commission_status);
        }
        const agentById = Object.fromEntries(ags.map(a => [a.id, a]));

        const out = [];
        for (const inv of invs) {
          const sell = (inv.line_items || []).reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
          const nett = invoiceCostLKR(inv);
          const gp = sell - nett;
          const suppliers = [...new Set((inv.cost_items || []).map(c => (c.supplier_label || '').trim()).filter(Boolean))].join(', ');
          const agentIds = new Set();
          if (inv.clients?.care_of_agent_id) agentIds.add(inv.clients.care_of_agent_id);
          for (const k of Object.keys(commByKey)) if (k.startsWith(inv.id + '|')) agentIds.add(k.split('|')[1]);
          for (const aid of agentIds) {
            const a = agentById[aid];
            const comm = commByKey[`${inv.id}|${aid}`];
            out.push({
              agent_id: aid,
              agent: a ? `${a.tls_code} — ${a.full_name}` : 'Unknown agent',
              invoice_id: inv.id,
              invoice_number: inv.invoice_number,
              date: inv.issued_date,
              type: TYPE_LABEL[inv.source_type] || inv.source_type || '',
              customer: inv.clients?.full_name || '',
              suppliers,
              nett, sell, gp,
              margin: sell > 0 ? (gp / sell) * 100 : 0,
              commission: comm?.amount || 0,
              commission_status: comm ? [...comm.statuses].join('/') : '',
              status: inv.status,
            });
          }
        }
        setRows(out);
      } catch (e) {
        setError(e.message || String(e));
      }
      setLoading(false);
    })();
  }, []);

  const visible = useMemo(() => {
    const lq = q.trim().toLowerCase();
    return rows.filter(r => (!agentFilter || r.agent_id === agentFilter) &&
      (!lq || `${r.invoice_number} ${r.customer} ${r.suppliers} ${r.agent}`.toLowerCase().includes(lq)));
  }, [rows, agentFilter, q]);

  const totals = useMemo(() => visible.reduce((t, r) => ({
    nett: t.nett + r.nett, sell: t.sell + r.sell, gp: t.gp + r.gp, commission: t.commission + r.commission,
  }), { nett: 0, sell: 0, gp: 0, commission: 0 }), [visible]);

  const selectedAgent = agents.find(a => a.id === agentFilter);

  const exportCsv = () => {
    downloadCsv([
      ['Agent', 'Invoice', 'Date', 'Type', 'Customer', 'Supplier(s)', 'Nett Cost (LKR)', 'Selling Price (LKR)', 'GP (LKR)', 'Margin %', 'Commission Earned (LKR)', 'Commission Status', 'Invoice Status'],
      ...visible.map(r => [r.agent, r.invoice_number, r.date, r.type, r.customer, r.suppliers, r.nett.toFixed(2), r.sell.toFixed(2), r.gp.toFixed(2), r.margin.toFixed(1), r.commission.toFixed(2), r.commission_status, r.status]),
      ['', 'TOTAL', '', '', '', '', totals.nett.toFixed(2), totals.sell.toFixed(2), totals.gp.toFixed(2), totals.sell > 0 ? (totals.gp / totals.sell * 100).toFixed(1) : '', totals.commission.toFixed(2), '', ''],
    ], `agent-sales-${selectedAgent ? selectedAgent.tls_code : 'all-agents'}-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Agent Sales</div>
          <div className="page-subtitle">Invoices per agent since the start — supplier, nett, selling price, GP, margin and commission</div>
        </div>
        <button className="btn btn-primary" onClick={exportCsv} disabled={loading || !visible.length}>Download CSV</button>
      </div>

      <div className="card" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
        <select className="form-input" style={{ maxWidth: 320 }} value={agentFilter}
          onChange={e => setParams(e.target.value ? { agent: e.target.value } : {})}>
          <option value="">All agents</option>
          {agents.map(a => <option key={a.id} value={a.id}>{a.tls_code} — {a.full_name}</option>)}
        </select>
        <input className="form-input" style={{ maxWidth: 320 }} placeholder="Search invoice, customer, supplier…" value={q} onChange={e => setQ(e.target.value)} />
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{visible.length} invoice{visible.length === 1 ? '' : 's'}</span>
      </div>

      <div className="stats-grid" style={{ marginBottom: 16 }}>
        <div className="stat-card"><div className="stat-label">Selling Price</div><div className="stat-value amount">{fmt(totals.sell)}</div></div>
        <div className="stat-card"><div className="stat-label">Nett Cost</div><div className="stat-value amount">{fmt(totals.nett)}</div></div>
        <div className="stat-card"><div className="stat-label">GP</div><div className="stat-value amount" style={{ color: totals.gp < 0 ? 'var(--danger)' : 'var(--success)' }}>{fmt(totals.gp)}</div></div>
        <div className="stat-card"><div className="stat-label">Commission Earned</div><div className="stat-value amount">{fmt(totals.commission)}</div></div>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
        <table className="table">
          <thead>
            <tr>
              {!agentFilter && <th>Agent</th>}
              <th>Invoice</th><th>Date</th><th>Type</th><th>Customer</th><th>Supplier(s)</th>
              <th style={{ textAlign: 'right' }}>Nett</th><th style={{ textAlign: 'right' }}>Selling Price</th>
              <th style={{ textAlign: 'right' }}>GP</th><th style={{ textAlign: 'right' }}>Margin</th>
              <th style={{ textAlign: 'right' }}>Commission</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={11} style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</td></tr>
            ) : !visible.length ? (
              <tr><td colSpan={11} style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>No invoices</td></tr>
            ) : visible.map(r => (
              <tr key={`${r.invoice_id}|${r.agent_id}`}>
                {!agentFilter && <td style={{ fontSize: 12 }}>{r.agent}</td>}
                <td><Link to={`/invoices/${r.invoice_id}`}>{r.invoice_number}</Link></td>
                <td style={{ fontSize: 12 }}>{r.date}</td>
                <td style={{ fontSize: 12 }}>{r.type}</td>
                <td>{r.customer}</td>
                <td style={{ fontSize: 12 }}>{r.suppliers || '—'}</td>
                <td style={{ textAlign: 'right' }} className="amount">{fmt(r.nett)}</td>
                <td style={{ textAlign: 'right' }} className="amount">{fmt(r.sell)}</td>
                <td style={{ textAlign: 'right', color: r.gp < 0 ? 'var(--danger)' : undefined }} className="amount">{fmt(r.gp)}</td>
                <td style={{ textAlign: 'right', fontSize: 12 }}>{r.margin.toFixed(1)}%</td>
                <td style={{ textAlign: 'right' }} className="amount">{r.commission ? fmt(r.commission) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
